<script setup lang="ts">
import { inject, computed } from 'vue';

interface VoucherPreviewBlok {
    _uid: string;
    component: string;
}

defineProps<{ blok: VoucherPreviewBlok }>();

const voucherPrefill = inject<{
    amount: number | null;
    type: 'digital' | 'physical' | null;
} | null>('voucherPrefill', null);

const hasContent = computed(
    () => voucherPrefill != null && (voucherPrefill.amount != null || voucherPrefill.type != null),
);

const isPhysical = computed(() => voucherPrefill?.type === 'physical');

const imageSrc = computed(() =>
    isPhysical.value
        ? '/images/redesign/physical_sample.webp'
        : '/images/redesign/digital_sample.webp',
);

const imagePng = computed(() =>
    isPhysical.value
        ? '/images/redesign/physical_sample.png'
        : '/images/redesign/digital_sample.png',
);

const typeLabel = computed(() => {
    if (voucherPrefill?.type === 'physical') {
        return 'Karte per Post';
    }
    if (voucherPrefill?.type === 'digital') {
        return 'Digitaler Gutschein (PDF)';
    }

    return null;
});

const configureHref = computed(() => {
    const q = new URLSearchParams();
    if (voucherPrefill?.amount != null) {
        q.set('amount', String(voucherPrefill.amount));
    }
    if (voucherPrefill?.type != null) {
        q.set('type', voucherPrefill.type);
    }
    const qs = q.toString();

    return '/kaufen/konfigurieren' + (qs ? '?' + qs : '');
});
</script>

<template>
    <section
        v-if="hasContent"
        v-editable="blok"
        class="py-16 xl:py-24"
        :class="'bg-[#FCF7F4]'"
    >
        <div class="mx-auto flex max-w-[1440px] flex-col items-center gap-10 px-4 md:flex-row md:justify-center md:gap-16 xl:gap-24">
            <!-- Voucher image -->
            <div class="relative shrink-0">
                <picture>
                    <source :srcset="imageSrc" type="image/webp" />
                    <img
                        :src="imagePng"
                        :alt="isPhysical ? 'Glowcard Geschenkkarte per Post' : 'Glowcard digitaler Gutschein'"
                        class="w-[260px] drop-shadow-xl xl:w-[320px]"
                        loading="lazy"
                        decoding="async"
                    />
                </picture>
                <!-- Amount badge -->
                <div
                    v-if="voucherPrefill?.amount != null"
                    class="absolute -bottom-3 -right-3 flex h-[72px] w-[72px] items-center justify-center rounded-full bg-[#F08E59] shadow-orange-button xl:h-[88px] xl:w-[88px]"
                >
                    <span class="text-xl font-bold leading-none text-white xl:text-2xl">
                        {{ voucherPrefill.amount }}&nbsp;€
                    </span>
                </div>
            </div>

            <!-- Text block -->
            <div class="flex flex-col items-center gap-5 text-center md:items-start md:text-left">
                <div class="space-y-2">
                    <p class="text-sm font-semibold uppercase tracking-wider text-[#F08E59]">
                        Deine Auswahl
                    </p>
                    <h2 class="text-[28px] font-semibold leading-tight text-[#004646] xl:text-4xl">
                        <span v-if="voucherPrefill?.amount != null">{{ voucherPrefill.amount }}&nbsp;€ Gutschein</span>
                        <span v-else>GLOWCARD Gutschein</span>
                    </h2>
                    <p v-if="typeLabel" class="text-base text-[#393734] xl:text-lg">
                        {{ typeLabel }}
                    </p>
                </div>
                <ul class="space-y-2 text-sm text-[#393734] xl:text-base">
                    <li class="flex items-center gap-2">
                        <svg class="shrink-0" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M16.5 3.07C15.15 2.38 13.62 2 12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10c0-.38-.02-.76-.06-1.12" stroke="#004646" stroke-width="1.5" stroke-linecap="round" />
                            <path d="M9 11l2.47 2.47c.29.29.77.29 1.06 0L21 5" stroke="#004646" stroke-width="1.5" stroke-linecap="round" />
                        </svg>
                        Über 3 Jahre einlösbar
                    </li>
                    <li class="flex items-center gap-2">
                        <svg class="shrink-0" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M16.5 3.07C15.15 2.38 13.62 2 12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10c0-.38-.02-.76-.06-1.12" stroke="#004646" stroke-width="1.5" stroke-linecap="round" />
                            <path d="M9 11l2.47 2.47c.29.29.77.29 1.06 0L21 5" stroke="#004646" stroke-width="1.5" stroke-linecap="round" />
                        </svg>
                        10.000+ Wohlfühlerlebnisse
                    </li>
                    <li class="flex items-center gap-2">
                        <svg class="shrink-0" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M16.5 3.07C15.15 2.38 13.62 2 12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10c0-.38-.02-.76-.06-1.12" stroke="#004646" stroke-width="1.5" stroke-linecap="round" />
                            <path d="M9 11l2.47 2.47c.29.29.77.29 1.06 0L21 5" stroke="#004646" stroke-width="1.5" stroke-linecap="round" />
                        </svg>
                        Persönlichen Gruß hinzufügen
                    </li>
                </ul>
                <a
                    :href="configureHref"
                    class="inline-flex items-center justify-center rounded-lg bg-[#F08E59] px-8 py-4 text-lg font-semibold text-white shadow-orange-button transition hover:opacity-95"
                >
                    Jetzt personalisieren
                </a>
            </div>
        </div>
    </section>
</template>
